import 'ol/ol.css'
import { Map, View } from 'ol'
import { get as getProjection, transform, transformExtent } from 'ol/proj'
import { register } from 'ol/proj/proj4'
import { boundingExtent } from 'ol/extent'
import { defaults as defaultInteractions } from 'ol/interaction'
import proj4 from 'proj4'
import { setConf, createLayer, getFormat } from 'util/layer/LayerCreator'
import { degToRad } from 'util/helpers'
import { shiftKeyOnly } from 'ol/events/condition'
import 'ol/MapBrowserEvent'

class MapEngine {
  constructor (conf) {
    this.conf = conf
    setConf(conf)
    this.map = null
    this.viewPadding = {
      top: 0,
      left: 0,
      bottom: 0,
      right: 0
    }
    this.controls = []
    if (conf.proj4defs) {
      registerProj4(conf.proj4defs)
    }
  }
  get () {
    return this.map
  }
  set (conf = {}) {
    conf = Object.assign({}, this.conf, conf)
    this.map = new Map({
      layers: conf.layers,
      controls: conf.controls || [],
      interactions: conf.interactions || defaultInteractions(),
      target: document.querySelector(conf.el),
      moveTolerance: 2,
      //pixelRatio: 2,
      view: new View({
        projection: conf.projection,
        constrainResolution: true,
        center: this.transform({
          coordinate: conf.center,
          from: 'EPSG:4326',
          to: conf.projection
        }),
        zoom: conf.zoom,
        rotation: degToRad(conf.rotation),
        maxZoom: conf.maxZoom,
        minZoom: conf.minZoom
      })
    })
    if (this.controls.length) {
      this.controls.forEach(control => {
        this.addControl(control)
      })
      this.controls = []
    }
  }
  getFit () {
    return {
      center: this.map.getView().getCenter(),
      zoom: this.map.getView().getZoom(),
      bbox: this.map.getView().calculateExtent(this.map.getSize()),
      srid: this.map.getView().getProjection().getCode()
    }
  }
  fitTo (item, clb) {
    if (item.bbox) {
      let bbox = item.bbox
      if (item.srid) {
        bbox = this.transform({ bbox: item.bbox, from: item.srid })
      }
      this.map.getView().fit(bbox, {
        padding: [
          this.viewPadding.top,
          this.viewPadding.right,
          this.viewPadding.bottom,
          this.viewPadding.left
        ],
        maxZoom: 15,
        duration: 300,
        callback: () => {
          if (typeof clb === 'function') {
            clb()
          }
        }
      })
    } else {
      let center = this.map.getView().getCenter()
      if (item.center) {
        center = item.srid ?
          this.transform({ coordinate: item.center, from: item.srid }) :
          item.center
      }
      center = this.getPaddedCenter(center, item.multiplier)
      const zoom = item.zoom || this.map.getView().getZoom()
      this.map.getView().animate({
        center: center,
        zoom: zoom,
        duration: 300
      }, () => {
        if (typeof clb === 'function') {
          clb()
        }
      })
    }
  }
  zoomIn () {
    if (this.map) {
      let centerPx = this.map.getPixelFromCoordinate(this.map.getView().getCenter())
      centerPx[0] = centerPx[0] + this.viewPadding.left / 4
      centerPx[1] = centerPx[1] - this.viewPadding.bottom / 4
      this.map.getView().animate({
        center: this.map.getCoordinateFromPixel(centerPx),
        zoom: this.map.getView().getZoom() + 1,
        duration: 300
      })
    }
  }
  zoomOut () {
    if (this.map) {
      let centerPx = this.map.getPixelFromCoordinate(this.map.getView().getCenter())
      centerPx[0] = centerPx[0] - this.viewPadding.left / 2
      centerPx[1] = centerPx[1] + this.viewPadding.bottom / 2
      this.map.getView().animate({
        center: this.map.getCoordinateFromPixel(centerPx),
        zoom: this.map.getView().getZoom() - 1,
        duration: 300
      })
    }
  }
  setViewPaddings (padds) {
    this.updateView(padds)
    this.viewPadding = Object.assign(this.viewPadding, padds)
  }
  updateView (padds) {
    if (this.map) {
      let centerPx = this.map.getPixelFromCoordinate(this.map.getView().getCenter())
      if (!centerPx) {
        centerPx = [0, 0]
      }
      if (typeof padds.left !== 'undefined') {
        centerPx[0] = centerPx[0] - (padds.left - this.viewPadding.left) / 4
      }
      if (typeof padds.bottom !== 'undefined') {
        centerPx[1] = centerPx[1] + (padds.bottom - this.viewPadding.bottom) / 4
      }
      const center = this.map.getCoordinateFromPixel(centerPx)
      this.map.getView().animate({
        center: center,
        duration: 300
      })
    }
  }
  getPaddedCenter (center, multiplier = 2) {
    if (this.map) {
      let centerInPx = this.map.getPixelFromCoordinate(center)
      if (!centerInPx) {
        centerInPx = [0, 0]
      }
      const newCenterInPx = [centerInPx[0], centerInPx[1]]
      newCenterInPx[0] = centerInPx[0] + this.viewPadding.left / multiplier
      newCenterInPx[1] = centerInPx[1] + this.viewPadding.bottom / multiplier
      return this.map.getCoordinateFromPixel(newCenterInPx)
    }
  }
  createLayer (conf) {
    return createLayer(conf, this.conf)
  }
  addLayer (layer, group = null) {
    if (!group) {
      group = this.map.getLayerGroup()
    }
    group.getLayers().push(layer)
  }
  removeLayer (layer, group = null) {
    if (!group) {
      group = this.map.getLayerGroup()
    }
    group.getLayers().remove(layer)
  }
  addControl (control) {
    if (this.map) {
      this.map.addControl(control)
    } else {
      this.controls.push(control)
    }
  }
  removeControl (control) {
    this.map.removeControl(control)
  }
  transform (item) {
    if (item.bbox && item.from) {
      return transformExtent(item.bbox, item.from, item.to || this.map.getView().getProjection())
    } else if (item.coordinate && item.from) {
      return transform(item.coordinate, item.from, item.to || this.map.getView().getProjection())
    }
  }
  bbox (geojson) {
    let coords = []
    if (!Array.isArray(geojson)) {
      geojson = [geojson]
    } else if (geojson.type && geojson.type === 'GeometryCollection') {
      geojson = geojson.geometries
    }
    geojson.forEach(item => {
      switch (item.type) {
        case 'Point':
          coords = coords.concat([item.coordinates])
          break
        case 'MultiPoint':
        case 'Linestring':
          coords = coords.concat(item.coordinates)
          break
        case 'MultiLinestring':
        case 'Polygon':
          coords = coords.concat(item.coordinates.flat(1))
          break
        case 'MultiPolygon':
          coords = coords.concat(item.coordinates.flat(2))
          break
      }
    })
    return boundingExtent(coords)
  }
  click (conf) {
    this.map.on('click', e => {
      let enabled = true
      if (conf.minZoom && conf.minZoom >= e.map.getView().getZoom()) {
        enabled = false
      }
      if (conf.maxZoom && conf.maxZoom <= e.map.getView().getZoom()) {
        enabled = false
      }
      if (enabled) {
        if (typeof conf.cb === 'function') {
          conf.cb(e.map.getFeaturesAtPixel(e.pixel), shiftKeyOnly(e))
        }
      }
    })
  }
  hover (conf) {
    this.map.on('pointermove', e => {
      if (e.dragging) {
        if (typeof conf.cb === 'function') {
          conf.cb(null)
        }
        return
      }
      let enabled = true
      if (conf.minZoom && conf.minZoom >= e.map.getView().getZoom()) {
        enabled = false
      }
      if (conf.maxZoom && conf.maxZoom <= e.map.getView().getZoom()) {
        enabled = false
      }
      if (!enabled) {
        if (typeof conf.cb === 'function') {
          conf.cb(null)
        }
        return
      }
      const px = e.map.getEventPixel(e.originalEvent)
      const feature = e.map.forEachFeatureAtPixel(px, feature => {
        return feature
      })
      if (feature) {
        if (typeof conf.cb === 'function') {
          conf.cb(feature, px)
        }
      } else {
        if (typeof conf.cb === 'function') {
          conf.cb(null)
        }
      }
    })
  }
  featureToGeoJSON (feature, opts = {}) {
    const format = getFormat('geojson')
    return format.writeFeatureObject(feature, {
      dataProjection: 'EPSG:4326',
      featureProjection: opts.projection || this.conf.projection
    })
  }
}

function registerProj4 (proj4defs) {
  Object.keys(proj4defs).forEach(srid => {
    proj4.defs(srid, proj4defs[srid].def)
  })
  register(proj4)
  Object.keys(proj4defs).forEach(srid => {
    if (proj4defs[srid].extent) {
      getProjection(srid).setExtent(proj4defs[srid].extent)
    }
  })
}

export default MapEngine
